import useDiscoveryFetch from './useDiscoveryFetch';
import { useRef, useState, useEffect, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import api from '../../api/axiosInstance';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Image,
    Spinner,
    Alert,
    Table,
    Pagination,
    Form,
} from 'react-bootstrap';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import MapGL, { Marker } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import icCountry from '../../assets/img/ic-globe.svg';
import icCity from '../../assets/img/ic-city.svg';
import icNumber from '../../assets/img/ic-number.svg';
import icBusinessPurpose from '../../assets/img/ic-briefcase.svg';
import icAdress from '../../assets/img/ic-home.svg';
import icLegalName from '../../assets/img/ic-building.svg';
import icHeartEmpty from '../../assets/img/ic-heart-empty.svg';
import icHeartFull from '../../assets/img/ic-heart-full.svg';
import { MAPBOX_TOKEN } from '../../config/mapbox';
import COUNTRY_COORDINATES from '../../config/countryCoordinates';
import NavTabs from '../../NavTabs';
import CountryDetailHero from './countryDetail/CountryDetailHero';
import useCommunityMembership from '../Communities/useCommunityMembership';
import CommunityCta from '../Communities/CommunityCta';
import CountryDetailMobileTabs from './countryDetail/CountryDetailMobileTabs';
import getCountryDetailNavItems from './countryDetail/countryDetailNavItems';
import CompanyDetailSection from './companyDetail/CompanyDetailSection';
import InfoRow from './companyDetail/InfoRow';
import MobileDiscoveryCityCard from './MobileDiscoveryCityCard';
import useMobileLoadMore from './useMobileLoadMore';
import MobileLoadMoreFooter from './MobileLoadMoreFooter';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';
import { trackEvent } from '../../services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from '../../services/analytics/analyticsEvents';

const DiscoveryCountryView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isMobile = useMediaQuery({ query: '(max-width: 991.98px)' });

    const [countryData, setCountryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Keep the last explicitly applied comparator when fresh API data arrives.
    const refreshSortRef = useRef(null);
    const [cities, setCities] = useState([]);
    const [citiesLoading, setCitiesLoading] = useState(true);
    const [citiesError, setCitiesError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [citiesPerPage, setCitiesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortOrderCity, setSortOrderCity] = useState('asc');
    const [sortOrderComments, setSortOrderComments] = useState('asc');
    const [favorites, setFavorites] = useState(new Set());

    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {
        const fetchCountry = async () => {
            try {
                if (!isReconnect || !countryData) setLoading(true);
                setError('');

                const response = await api.get(`/api/discovery/countries/${id}`, { signal });
                if (!isCurrent()) return;
                setCountryData(response.data);

                if (!isReconnect) trackEvent(ANALYTICS_EVENTS.DISCOVERY_COUNTRY_VIEWED, {
                    countryCode: String(response.data?.iso2 || response.data?.country || '').toUpperCase(),
                    countryName: response.data?.countryName || response.data?.country?.name || '',
                    level: 2,
                    source: 'country_detail_page',
                });
            } catch (err) {
                if (!isCurrent()) return;
                console.error('Error fetching country:', err);
                setError('An error occurred while fetching country details.');
            } finally {
                if (!isCurrent()) return;
                setLoading(false);
            }
        };

        fetchCountry();
    }, [id]);

    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {
        const fetchCitiesAndFavorites = async () => {
            try {
                if (!isReconnect || !countryData) setCitiesLoading(true);
                const [citiesRes, currentUserRes] = await Promise.all([
                    api.get(`/api/discovery/countries/${id}/cities`, { signal }),
                    api.get('/api/users/me', { signal }),
                ]);
                if (!isCurrent()) return;
                setCities(refreshSortRef.current ? [...(citiesRes.data || [])].sort(refreshSortRef.current) : citiesRes.data || []);
                setCitiesError(null);
                const safeFavoriteCities = Array.isArray(currentUserRes?.data?.favoriteCities)
                    ? currentUserRes.data.favoriteCities.map((cityId) => String(cityId))
                    : [];
                setFavorites(new Set(safeFavoriteCities));
            } catch (err) {
                if (!isCurrent()) return;
                console.error('Error fetching cities:', err);
                setCitiesError('Error fetching cities for this country.');
            } finally {
                if (!isCurrent()) return;
                setCitiesLoading(false);
            }
        };

        fetchCitiesAndFavorites();
    }, [id]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, citiesPerPage]);

    const formData = {
        iso2: countryData?.iso2 || countryData?.country || '',
        countryName: countryData?.countryName || countryData?.country?.name || '',
        continent: countryData?.continent || '',
        languagesSpoken: Array.isArray(countryData?.languagesSpoken) ? countryData.languagesSpoken : [],
        url: countryData?.url || '',
        citizensNumber: countryData?.citizensNumber || '',
        currency: countryData?.currency || '',
        visaRequired: countryData?.visaRequired || '',
        visaDuration: countryData?.visaDuration || '',
        visaType: countryData?.visaType || '',
        noPermitPeriod: countryData?.noPermitPeriod || '',
        weekWorkingHours: countryData?.weekWorkingHours || '',
        minimumWage: countryData?.minimumWage || '',
        wageAmount: countryData?.wageAmount || '',
        internshipsRegulation: countryData?.internshipsRegulation || '',
        incomeTaxLiability: countryData?.incomeTaxLiability || '',
        doubleTaxationAgreementUrl: countryData?.doubleTaxationAgreementUrl || '',
        healthInsurance: countryData?.healthInsurance || '',
        accidentInsurance: countryData?.accidentInsurance || '',
        destinationCountryCurrency: countryData?.destinationCountryCurrency || '',
        bankAccountDocuments: countryData?.bankAccountDocuments || '',
        exchangeRate: countryData?.exchangeRate || '',
        platformUrls: Array.isArray(countryData?.platformUrls) ? countryData.platformUrls : [],
        socialMediaPlatforms: Array.isArray(countryData?.socialMediaPlatforms) ? countryData.socialMediaPlatforms : [],
        jobPlatforms: Array.isArray(countryData?.jobPlatforms) ? countryData.jobPlatforms : [],
        averageRentalPricesUrban: countryData?.averageRentalPricesUrban || '',
        averageRentalPricesRural: countryData?.averageRentalPricesRural || '',
        transportMonthly: countryData?.transportMonthly || '',
        livingCosts: countryData?.livingCosts || '',
        rentingRestrictions: countryData?.rentingRestrictions || '',
        rentingRestrictionsText: countryData?.rentingRestrictionsText || '',
        importantLanguages: countryData?.importantLanguages || '',
        englishWide: countryData?.englishWide || '',
        industries: Array.isArray(countryData?.industries) ? countryData.industries : [],
        companyCount: countryData?.companyCount || 0,
        cityCount: countryData?.cityCount || 0,
    };

    const iso2 = String(countryData?.iso2 || countryData?.country || '').toUpperCase();

    // Looked up once here (not inside CommunityCta) so rendering the CTA from
    // both the mobile hero and the desktop header below never issues two
    // GET /api/conversations/communities/country/:refId requests. Uses the
    // route param directly (same identifier already sent to
    // /api/discovery/countries/:id, which the backend matches by iso2 or
    // ObjectId either way) rather than waiting on countryData to resolve it,
    // so the lookup does not depend on a second fetch having already succeeded.
    const communityStatus = useCommunityMembership({ type: 'country', refId: id });

    const coordsFromLookup = COUNTRY_COORDINATES[iso2];
    const latitude = coordsFromLookup?.lat ?? Number(countryData?.latitude);
    const longitude = coordsFromLookup?.lng ?? Number(countryData?.longitude);
    const hasCoordinates = !Number.isNaN(latitude) && !Number.isNaN(longitude);

    const languagesText = useMemo(() => {
        if (!Array.isArray(formData.languagesSpoken) || !formData.languagesSpoken.length) return 'N/A';
        return formData.languagesSpoken.join(', ');
    }, [formData.languagesSpoken]);

    const discoveryCountryNavItems = getCountryDetailNavItems(id);

    const normalizedQuery = searchQuery.trim().toLowerCase();
    const filteredCities = !normalizedQuery
        ? cities
        : cities.filter((city) => {
            const name = (city.name || '').toLowerCase();
            const cityId = (city.cityId || '').toLowerCase();
            const comments = String(city.comments ?? '').toLowerCase();
            return name.includes(normalizedQuery) || cityId.includes(normalizedQuery) || comments.includes(normalizedQuery);
        });
    const totalPages = Math.ceil(filteredCities.length / citiesPerPage) || 1;
    const indexOfLastCity = currentPage * citiesPerPage;
    const currentCities = filteredCities.slice(indexOfLastCity - citiesPerPage, indexOfLastCity);
    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
    const mobileCityPagination = useMobileLoadMore({
        items: cities,
        filterKey: String(id),
        storageKey: 'discovery-country-detail-cities-mobile-list',
        isMobile,
    });

    const sortCitiesByName = () => {
        const newOrder = sortOrderCity === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const nameA = String(a.name || '').toLowerCase();
            const nameB = String(b.name || '').toLowerCase();
            return newOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        };
        setCities((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderCity(newOrder);
    };

    const sortCitiesByComments = () => {
        const newOrder = sortOrderComments === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const cA = Number(a.comments) || 0;
            const cB = Number(b.comments) || 0;
            return newOrder === 'asc' ? cA - cB : cB - cA;
        };
        setCities((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderComments(newOrder);
    };

    const toggleFavorite = async (cityId) => {
        const cityIdStr = String(cityId);
        const previousFavorites = new Set(favorites);
        const optimisticFavorites = new Set(previousFavorites);
        if (optimisticFavorites.has(cityIdStr)) {
            optimisticFavorites.delete(cityIdStr);
        } else {
            optimisticFavorites.add(cityIdStr);
        }
        setFavorites(optimisticFavorites);
        try {
            const res = await api.patch(`/api/users/me/favorite-cities/${cityIdStr}`);
            const safeFavoriteCities = Array.isArray(res?.data?.favoriteCities)
                ? res.data.favoriteCities.map((favId) => String(favId))
                : [];
            setFavorites(new Set(safeFavoriteCities));
        } catch (error) {
            console.error('Error toggling favorite city:', error);
            setFavorites(previousFavorites);
        }
    };

    const getPaginationItems = () => {
        const siblingCount = 2;
        const boundaryCount = 1;
        if (totalPages <= 1) return [1];
        const range = (start, end) => {
            const out = [];
            for (let i = start; i <= end; i++) out.push(i);
            return out;
        };
        const startPages = range(1, Math.min(boundaryCount, totalPages));
        const endPages = range(Math.max(totalPages - boundaryCount + 1, boundaryCount + 1), totalPages);
        const siblingsStart = Math.max(
            Math.min(currentPage - siblingCount, totalPages - boundaryCount - siblingCount * 2 - 1),
            boundaryCount + 2
        );
        const siblingsEnd = Math.min(
            Math.max(currentPage + siblingCount, boundaryCount + siblingCount * 2 + 2),
            totalPages - boundaryCount - 1
        );
        const items = [
            ...startPages,
            ...(siblingsStart > boundaryCount + 2 ? ['ellipsis'] : boundaryCount + 1 < totalPages - boundaryCount ? [boundaryCount + 1] : []),
            ...range(siblingsStart, siblingsEnd),
            ...(siblingsEnd < totalPages - boundaryCount - 1 ? ['ellipsis'] : totalPages - boundaryCount > boundaryCount ? [totalPages - boundaryCount] : []),
            ...endPages,
        ];
        return items.filter((v, i) => items.indexOf(v) === i);
    };

    // Opt this page into the shared mobile AI assistant (header trigger + bottom sheet).
    useMobileAiPageContext({
        pageType: 'discovery-country',
        discoveryType: 'countries',
        countryId: id,
        countryName: countryData?.countryName || countryData?.country?.name || '',
        activeTab: 'General info',
        suggestions: getDiscoveryAiSuggestions({
            discoveryType: 'countries',
            selectedCountry: countryData?.countryName || countryData?.country?.name || '',
        }),
    });

    if (loading) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col className="py-5 text-center">
                                <Spinner animation="border" />
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="danger" className="mt-4">
                                    {error}
                                </Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (!countryData) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="warning" className="mt-4">
                                    Country not found.
                                </Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    const sectionContent = (
        <>
            <Row className="mb-4 country-general-info-desktop">
                <Col>
                    <Card className="h-100">
                                                            <Card.Body>
                                                                <Row>
                                                                    <Col md={12}>
                                                                        <h3><span className="item-number">1.</span> General info</h3>
                                                                        <hr />
                                                                    </Col>
                                                                </Row>

                                                                <Row>
                                                                    <Col md={8}>
                                                                        <Row className="mb-4">
                                                                            <Col md={6}>
                                                                                <div className="icon-text">
                                                                                    <span className="icon" aria-hidden="true">
                                                                                        <Image src={icLegalName} alt="Country Name" />
                                                                                    </span>
                                                                                    <span className="text">
                                                                                        Country Name
                                                                                        <br />
                                                                                        <strong>{formData.countryName || '—'}</strong>
                                                                                    </span>
                                                                                </div>
                                                                            </Col>

                                                                            <Col md={6}>
                                                                                <div className="icon-text">
                                                                                    <span className="icon" aria-hidden="true">
                                                                                        <Image src={icNumber} alt="Number of Citizens" />
                                                                                    </span>
                                                                                    <span className="text">
                                                                                        Number of Citizens
                                                                                        <br />
                                                                                        <strong>{formData.citizensNumber ? Number(formData.citizensNumber).toLocaleString() : '—'}</strong>
                                                                                    </span>
                                                                                </div>
                                                                            </Col>
                                                                        </Row>

                                                                        <Row className="mb-4">
                                                                            <Col md={6}>
                                                                                <div className="icon-text">
                                                                                    <span className="icon" aria-hidden="true">
                                                                                        <Image src={icCity} alt="Languages spoken" />
                                                                                    </span>
                                                                                    <span className="text">
                                                                                        Language(s) spoken
                                                                                        <br />
                                                                                        <strong>{languagesText}</strong>
                                                                                    </span>
                                                                                </div>
                                                                            </Col>

                                                                            <Col md={6}>
                                                                                <div className="icon-text">
                                                                                    <span className="icon" aria-hidden="true">
                                                                                        <Image src={icCountry} alt="Continent" />
                                                                                    </span>
                                                                                    <span className="text">
                                                                                        Continent allocation
                                                                                        <br />
                                                                                        <strong>{formData.continent || '—'}</strong>
                                                                                    </span>
                                                                                </div>
                                                                            </Col>
                                                                        </Row>

                                                                        <Row>
                                                                            <Col md={6}>
                                                                                <div className="icon-text">
                                                                                    <span className="icon" aria-hidden="true">
                                                                                        <Image src={icAdress} alt="Currency" />
                                                                                    </span>
                                                                                    <span className="text">
                                                                                        Currency
                                                                                        <br />
                                                                                        <strong>{formData.currency || '—'}</strong>
                                                                                    </span>
                                                                                </div>
                                                                            </Col>

                                                                            <Col md={6}>
                                                                                <div className="icon-text">
                                                                                    <span className="icon" aria-hidden="true">
                                                                                        <Image src={icBusinessPurpose} alt="Country URL" />
                                                                                    </span>
                                                                                    <span className="text">
                                                                                        Country URL
                                                                                        <br />
                                                                                        {formData.url ? (
                                                                                            <a
                                                                                                href={formData.url.startsWith('http') ? formData.url : `https://${formData.url}`}
                                                                                                target="_blank"
                                                                                                rel="noopener noreferrer"
                                                                                                className="fw-bold"
                                                                                            >
                                                                                                {formData.url}
                                                                                            </a>
                                                                                        ) : (
                                                                                            <strong>—</strong>
                                                                                        )}
                                                                                    </span>
                                                                                </div>
                                                                            </Col>
                                                                        </Row>
                                                                    </Col>

                                                                    {hasCoordinates && (
                                                                        <Col md={4} className="p-0">
                                                                            <MapGL
                                                                                initialViewState={{
                                                                                    latitude,
                                                                                    longitude,
                                                                                    zoom: 6,
                                                                                }}
                                                                                mapStyle="mapbox://styles/mapbox/light-v10"
                                                                                mapboxAccessToken={MAPBOX_TOKEN}
                                                                                style={{ width: '100%', height: 220, borderRadius: 8 }}
                                                                                dragPan={true}
                                                                                scrollZoom={true}
                                                                                doubleClickZoom={true}
                                                                                touchZoomRotate={true}
                                                                            >
                                                                                <Marker latitude={latitude} longitude={longitude}>
                                                                                    <div className="marker-bg" />
                                                                                </Marker>
                                                                            </MapGL>
                                                                        </Col>
                                                                    )}
                                                                </Row>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>

                                                {/* List of cities */}
                                                <Row className={isMobile ? undefined : 'mb-4'}>
                                                    <Col>
                                                        <Card>
                                                            <Card.Body>
                                                                <Row className="align-items-center">
                                                                    <Col>
                                                                        <h3><span className="item-number">2.</span> List of cities</h3>
                                                                        <hr />
                                                                    </Col>
                                                                </Row>
                                                                <Row>
                                                                    <Col>
                                                                        {citiesLoading ? (
                                                                            <div>Loading cities...</div>
                                                                        ) : citiesError ? (
                                                                            <Alert variant="danger">{citiesError}</Alert>
                                                                        ) : cities.length === 0 ? (
                                                                            <p className="mb-0">No cities available.</p>
                                                                        ) : isMobile ? (
                                                                            <div className="mobile-company-list mobile-company-list--country-detail">
                                                                                {mobileCityPagination.mobileItems.map((city) => (
                                                                                    <MobileDiscoveryCityCard
                                                                                        key={city._id}
                                                                                        city={{
                                                                                            ...city,
                                                                                            countryName: city.countryName || formData.countryName,
                                                                                        }}
                                                                                        secondStat={{ label: 'Comments', value: city.comments ?? 0 }}
                                                                                        isFavorite={favorites.has(String(city._id))}
                                                                                        onFavoriteToggle={toggleFavorite}
                                                                                        onOpenCity={(selectedCity) => {
                                                                                            mobileCityPagination.rememberScrollPosition();
                                                                                            navigate(`/discovery/countries/${id}/cities/${selectedCity._id}`);
                                                                                        }}
                                                                                    />
                                                                                ))}
                                                                                <MobileLoadMoreFooter
                                                                                    visibleCount={mobileCityPagination.visibleCount}
                                                                                    totalCount={mobileCityPagination.totalCount}
                                                                                    itemLabel="cities"
                                                                                    onLoadMore={mobileCityPagination.loadMore}
                                                                                    isLoading={mobileCityPagination.isLoadingMore}
                                                                                    hasMore={mobileCityPagination.hasMore}
                                                                                />
                                                                            </div>
                                                                        ) : (
                                                                            <>
                                                                                <div className="table-responsive list">
                                                                                    <Table className="discovery-table discovery-table--cities">
                                                                                        <thead>
                                                                                            <tr>
                                                                                                <th>ID</th>
                                                                                                <th onClick={sortCitiesByName} style={{ cursor: 'pointer' }}>
                                                                                                    <div>
                                                                                                        City Region
                                                                                                        <span>
                                                                                                            <FaCaretUp />
                                                                                                            <FaCaretDown />
                                                                                                        </span>
                                                                                                    </div>
                                                                                                </th>
                                                                                                <th onClick={sortCitiesByComments} style={{ cursor: 'pointer' }}>
                                                                                                    <div>
                                                                                                        Comments
                                                                                                        <span>
                                                                                                            <FaCaretUp />
                                                                                                            <FaCaretDown />
                                                                                                        </span>
                                                                                                    </div>
                                                                                                </th>
                                                                                                <th>Favorite</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {currentCities.map((city) => (
                                                                                                <tr
                                                                                                    className="company-row discovery-row"
                                                                                                    key={city._id}
                                                                                                    onClick={() => navigate(`/discovery/countries/${id}/cities/${city._id}`)}
                                                                                                    style={{ cursor: 'pointer' }}
                                                                                                >
                                                                                                    <td>{city.cityId || '—'}</td>
                                                                                                    <td>{city.name || city.cityName || '—'}</td>
                                                                                                    <td>{city.comments ?? 0}</td>
                                                                                                    <td>
                                                                                                        <span
                                                                                                            className="cursor-pointer d-inline-flex align-items-center"
                                                                                                            style={{ cursor: 'pointer' }}
                                                                                                            onClick={(e) => {
                                                                                                                e.stopPropagation();
                                                                                                                toggleFavorite(city._id);
                                                                                                            }}
                                                                                                        >
                                                                                                            <img
                                                                                                                src={favorites.has(String(city._id)) ? icHeartFull : icHeartEmpty}
                                                                                                                alt="favorite"
                                                                                                                style={{ width: '16px', height: '16px' }}
                                                                                                            />
                                                                                                        </span>
                                                                                                    </td>
                                                                                                </tr>
                                                                                            ))}
                                                                                        </tbody>
                                                                                    </Table>
                                                                                </div>
                                                                                <Row className="pagination-wrapper">
                                                                                    <Col className="d-flex align-items-center">
                                                                                        <span>
                                                                                            Total {filteredCities.length} {filteredCities.length === 1 ? 'city' : 'cities'}
                                                                                        </span>
                                                                                        <Pagination>
                                                                                            <Pagination.Prev
                                                                                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                                                                disabled={currentPage === 1}
                                                                                            >
                                                                                                <BsChevronLeft />
                                                                                            </Pagination.Prev>
                                                                                            {getPaginationItems().map((item, index) =>
                                                                                                item === 'ellipsis' ? (
                                                                                                    <Pagination.Ellipsis key={`e-${index}`} disabled />
                                                                                                ) : (
                                                                                                    <Pagination.Item
                                                                                                        key={item}
                                                                                                        active={item === currentPage}
                                                                                                        onClick={() => handlePageChange(item)}
                                                                                                    >
                                                                                                        {item}
                                                                                                    </Pagination.Item>
                                                                                                )
                                                                                            )}
                                                                                            <Pagination.Next
                                                                                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                                                                                disabled={currentPage === totalPages || totalPages === 0}
                                                                                            >
                                                                                                <BsChevronRight />
                                                                                            </Pagination.Next>
                                                                                        </Pagination>
                                                                                    </Col>
                                                                                    <Col className="pagination-controls">
                                                                                        <div className="pagination-controls-per-page">
                                                                                            <Form.Select
                                                                                                value={citiesPerPage}
                                                                                                onChange={(e) => {
                                                                                                    setCitiesPerPage(Number(e.target.value));
                                                                                                    setCurrentPage(1);
                                                                                                }}
                                                                                            >
                                                                                                <option value="10">10 / page</option>
                                                                                                <option value="20">20 / page</option>
                                                                                                <option value="40">40 / page</option>
                                                                                            </Form.Select>
                                                                                        </div>
                                                                                        <label className="pagination-controls-goto-label" htmlFor="goto-page-country-view-cities">
                                                                                            Go to
                                                                                        </label>
                                                                                        <Form.Control
                                                                                            id="goto-page-country-view-cities"
                                                                                            className="pagination-controls-goto-input"
                                                                                            type="number"
                                                                                            value={currentPage}
                                                                                            onChange={(e) => {
                                                                                                const newPage = Number(e.target.value);
                                                                                                if (newPage >= 1 && newPage <= totalPages) {
                                                                                                    setCurrentPage(newPage);
                                                                                                }
                                                                                            }}
                                                                                            onBlur={() => {
                                                                                                if (currentPage < 1) setCurrentPage(1);
                                                                                                if (currentPage > totalPages) setCurrentPage(totalPages || 1);
                                                                                            }}
                                                                                        />
                                                                                    </Col>
                                                                                </Row>
                                                                            </>
                                                                        )}
                                                                    </Col>
                                                                </Row>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>
        </>
    );

    const mobileGeneralInfo = (
        <CompanyDetailSection
            title="General info"
            number={1}
            className="country-general-info-section"
        >
            <InfoRow
                icon={icLegalName}
                iconAlt="Country Name"
                label="Country Name"
                value={formData.countryName || '—'}
            />
            <InfoRow
                icon={icNumber}
                iconAlt="Number of Citizens"
                label="Number of Citizens"
                value={formData.citizensNumber ? Number(formData.citizensNumber).toLocaleString() : '—'}
            />
            <InfoRow
                icon={icCity}
                iconAlt="Languages spoken"
                label="Language(s) spoken"
                value={languagesText}
            />
            <InfoRow
                icon={icCountry}
                iconAlt="Continent"
                label="Continent allocation"
                value={formData.continent || '—'}
            />
            <InfoRow
                icon={icAdress}
                iconAlt="Currency"
                label="Currency"
                value={formData.currency || '—'}
            />
            <InfoRow
                icon={icBusinessPurpose}
                iconAlt="Country URL"
                label="Country URL"
                value={formData.url || '—'}
                href={formData.url ? (formData.url.startsWith('http') ? formData.url : `https://${formData.url}`) : undefined}
            />
        </CompanyDetailSection>
    );

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb countryName={formData.countryName} subPage="General info" />
                        </Col>
                    </Row>

                    {!isMobile ? (
                        <Row>
                            <Col>
                                <Card className="mb-4">
                                    <Card.Body className="pt-0">
                                        <Row>
                                            <Col xs={12}>
                                                <>
                                                    <Row>
                                                        <Col>
                                                            <Card className="card-premium">
                                                                <Card.Body>
                                                                    <Row className="align-items-center">
                                                                        <Col>
                                                                            <h3 className="text-start">Country</h3>
                                                                        </Col>
                                                                        <Col xs="auto">
                                                                            <CommunityCta status={communityStatus} />
                                                                        </Col>
                                                                    </Row>
                                                                    <hr />

                                                                    <Row className="mb-4">
                                                                        <Col md={8} className="d-flex">

                                                                            <div>
                                                                                <h1>{formData.countryName || '—'}</h1>
                                                                                <p>
                                                                                    {formData.url ? (
                                                                                        <a
                                                                                            href={formData.url.startsWith('http') ? formData.url : `https://${formData.url}`}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                        >
                                                                                            {formData.url}
                                                                                        </a>
                                                                                    ) : (
                                                                                        '—'
                                                                                    )}
                                                                                    {formData.cityCount > 0 && (
                                                                                        <>
                                                                                            <span className="dot"></span>
                                                                                            Live cities: <strong>{formData.cityCount}</strong>
                                                                                        </>
                                                                                    )}
                                                                                </p>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                    </Row>

                                                    <Row className="mb-5">
                                                        <Col>
                                                            <NavTabs items={discoveryCountryNavItems} />
                                                        </Col>
                                                    </Row>

                                                    {sectionContent}
                                                </>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    ) : (
                        <div className="country-detail-mobile">
                            <CountryDetailHero
                                countryName={formData.countryName}
                                url={formData.url}
                                cityCount={formData.cityCount}
                                communityAction={<CommunityCta status={communityStatus} />}
                            />

                            <CountryDetailMobileTabs items={discoveryCountryNavItems} />

                            <Card className="country-detail-content-card mb-4">
                                <Card.Body>
                                    {mobileGeneralInfo}
                                    {sectionContent}
                                </Card.Body>
                            </Card>
                        </div>
                    )}
                </Container>
            </main>
        </>
    );
};

export default DiscoveryCountryView;
